import { AfterContentChecked, Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Page } from '../../../../core/components/table-footer-paging/table-footer-paging.component';
import { BxLogger } from '../../../../core/services/bx-logger.service';
import { BxUnverifiedAlertService } from '../../../../core/services/bx-unverified-alert.service';
import { ColumnWidthsService } from '../../../../core/services/column-widths.service';
import { DialogService } from '../../../../core/services/dialog.service';
import { LocalStorageService } from '../../../../core/services/local-storage.service';
import { FeatureService, PermissionService } from '../../../../core/services/permission';
import { StoresService } from '../../../../core/services/stores';
import { TablesHelperService } from '../../../../core/services/tables-helper.service';
import { UserService } from '../../../../core/services/user';
import { ExportContactsOptions, ExportFormat, Tables } from '../../../../data/models';
import { ContactApiService } from '../../../../data/repositories/contacts/contacts.api.service';
import { RepoContactsService } from '../../../../data/repositories/repo-contacts.service';
import { RepoExportService } from '../../../../data/repositories/repo-export.service';
import { RepoSettingsService } from '../../../../data/repositories/repo-settings.service';
import { RepoStorageService } from '../../../../data/repositories/repo-storage.service';
import { SharedDataService } from '../../../../data/services/shared-data.service';
import { LayoutService } from '../../../layout/layout.service';
import { ContactsService } from '../../contacts.service';

type ColumnWidths = {
    Name: number;
    BusinessName: number;
    ContactType: number;
    CardExpiryNext: number;
    WorkPhone: number;
    Mobile: number;
    Email: number;
};

@UntilDestroy()
@Component({
    selector: 'app-contact-list2',
    templateUrl: './contact-list2.component.html',
    standalone: false,
})
export class ContactList2Component implements OnInit, AfterContentChecked {
    rows = [];
    selected = [];
    isLoading = false;
    // @ts-ignore TS2564
    page: Page;
    title = 'Contacts';
    // @ts-ignore TS2564
    search: string;
    // @ts-ignore TS7008
    currentComponentWidth;
    defaultSort: { orderBy?: string; orderByDesc?: string } = {};
    sortField?: string;
    deletedView = false;
    contactsWidths = {} as ColumnWidths;
    // @ts-ignore TS2564
    deregisterGetContacts: Function;
    tableHeight = 'auto';
    modulePermitted = true;
    // @ts-ignore TS2564
    canSetReminders: boolean;
    // @ts-ignore TS2564
    canSendSms: boolean;
    // @ts-ignore TS2564
    canSendEmail: boolean;

    // url params
    // @ts-ignore TS7008
    group;
    // @ts-ignore TS7008
    status;

    // dropdown options for more actions
    moreActions = [
        {
            value: 0,
            label: 'Import from Excel',
            condition: this.permissionService.isAvailable('contacts.maintain-contacts.import-from-excel'),
        },
        {
            value: 1,
            label: 'Export to Excel',
            condition: this.permissionService.isAvailable('contacts.maintain-contacts.export-contacts'),
        },
    ];

    settingsActions = [
        {
            groupName: 'Display only',
            actions: [
                { label: 'Active Items', condition: true, value: 0 },
                { label: 'Deleted Items', condition: true, value: 1 },
            ],
        },
    ];

    simpleItems = [
        {
            actions: [{ label: 'Reset View to Default', condition: true, value: 0 }],
        },
    ];

    @ViewChild('myTable') table: any;
    // @ts-ignore TS7008
    @ViewChild('tableWrapper') tableWrapper;

    constructor(
        private contactsRepo: RepoContactsService,
        public layoutService: LayoutService,
        private storesService: StoresService,
        private settings: RepoSettingsService,
        public permissionService: PermissionService,
        public featureService: FeatureService,
        public contactService: ContactsService,
        public storage: RepoStorageService,
        private localStorageService: LocalStorageService,
        private columnWidthsService: ColumnWidthsService,
        private repoExportService: RepoExportService,
        private readonly sharedData: SharedDataService,
        private readonly bxUnverifiedAlertService: BxUnverifiedAlertService,
        private tablesHelperService: TablesHelperService,
        private dialogService: DialogService,
        private readonly userService: UserService,
        private readonly route: ActivatedRoute,
        private readonly contactApi: ContactApiService,
        private logger: BxLogger
    ) {
        setTimeout(() => {
            this.modulePermitted =
                this.permissionService.isAvailable('contacts.contacts-access.contacts-menu') &&
                this.featureService.isStateAvailable('contacts');
        }, 500);
    }

    ngOnInit() {
        this.canSetReminders =
            this.permissionService.isAvailable('contacts.maintain-contacts.set-contact-reminder') &&
            this.featureService.isStateAvailable('contacts.maintain-contacts.set-contact-reminder');
        this.canSendSms =
            this.permissionService.isAvailable('contacts.maintain-contacts.send-contact-sms') &&
            this.featureService.isStateAvailable('contacts.maintain-contacts.send-contact-sms') &&
            this.featureService.isAvailable('sms-features') &&
            this.userService.isSmsEnabled;
        this.canSendEmail =
            this.permissionService.isAvailable('contacts.maintain-contacts.send-contact-email') &&
            this.featureService.isStateAvailable('contacts.maintain-contacts.send-contact-email');

        if (this.localStorageService.getObject('contacts-table')) {
            this.contactsWidths = this.localStorageService.getObject('contacts-table');
        }

        this.page = new Page();
        this.setDefaults();
        this.setPage({ offset: 0 });

        this.storesService.refreshContacts$.pipe(untilDestroyed(this)).subscribe(() => {
            this.resetPageNumber();
            this.load();
        });
    }

    // Need to re calculate the table size when the nav 2 toggle is clicked
    ngAfterContentChecked() {
        if (this.table && this.tableWrapper.nativeElement.clientWidth !== this.currentComponentWidth) {
            this.currentComponentWidth = this.tableWrapper.nativeElement.clientWidth;
            this.rows = [...this.rows];
        }
    }

    setDefaults() {
        this.page.pageNumber = 0;
        this.page.size = this.settings.ContactsListViewPageCount;

        this.defaultSort = {
            orderBy: 'Name',
            orderByDesc: 'asc',
        };

        if (this.route.snapshot.queryParams.status && this.route.snapshot.queryParams.status === 'notvalid') {
            this.defaultSort = {
                orderBy: 'CardExpiryNext',
                orderByDesc: 'asc',
            };
        }

        if (this.route.snapshot.queryParams.status && this.route.snapshot.queryParams.status === 'noemail') {
            this.defaultSort = {
                orderBy: 'Email',
                orderByDesc: 'asc',
            };
        }

        this.sortField = this.defaultSort.orderBy;
    }

    load() {
        // Reset
        this.group = this.route.snapshot.queryParams.group;
        this.status = this.route.snapshot.queryParams.status;
        this.title = 'Contacts';
        this.storesService.updateSubpanelFilterStatus({ submenu: 'contacts', enabled: true });

        if (this.status && this.status === 'notvalid') {
            this.title = 'Contact Cards Expiring or Expired';
        }
        this.loadPage();
    }

    // @ts-ignore TS7006
    pageLimit(event) {
        this.page.size = Number.parseInt(event.target.textContent);
        this.page.pageNumber = 0;
        this.settings.ContactsListViewPageCount = this.page.size;
        this.loadPage();
    }

    loadPage() {
        this.isLoading = true;

        const config: {
            orderByDesc?: string;
            orderBy?: string;
            pageSize?: number;
            pageSkip?: number;
            isDeleted?: boolean;
            search?: string;
            group?: string;
            notvalid?: boolean;
        } = {};

        if (this.defaultSort) {
            config.orderByDesc = this.defaultSort.orderByDesc;
            config.orderBy = this.defaultSort.orderBy;
        }

        config.pageSize = this.page.size;

        config.pageSkip = this.page.pageNumber;

        config.isDeleted = this.deletedView;

        if (this.search) {
            config.search = this.search;
        }

        config.group = this.group;

        config.notvalid = this.status && this.status === 'notvalid';

        this.contactsRepo.SearchV2(config).then((resp) => {
            this.resolveSearch(resp);
            this.isLoading = false;
        });
    }

    resolveSearch(data: any) {
        this.page.totalElements = data.inlineCount;
        this.rows = data.results;
        if (this.rows && this.rows.length) {
            // this.tableHeight = this.rows.length * 42 + 52 + 52 + 'px';
        }
    }

    // @ts-ignore TS7006
    setSearch(event) {
        this.search = event;
        this.page.pageNumber = 0;
        this.load();
    }

    newContact() {
        this.contactService
            .newContact(null)
            .then(() => {})
            .catch(() => {});
    }

    // TODO:: move to shared component to slim the product
    // @ts-ignore TS7006
    emailContact(contact) {
        if (!this.sharedData.UserData.identityInfo.email_verified) {
            this.bxUnverifiedAlertService.trigger(this.sharedData.UserData.user);
            return;
        }

        this.contactService.emailContact(contact);
    }
    // TODO:: move to shared component to slim the product
    // @ts-ignore TS7006
    smsContact(contact) {
        if (!this.sharedData.UserData.identityInfo.email_verified) {
            this.bxUnverifiedAlertService.trigger(this.sharedData.UserData.user);
            return;
        }

        this.contactService.smsContact(contact).subscribe();
    }
    // TODO:: move to shared component to slim the product
    // @ts-ignore TS7006
    addReminder(contact) {
        if (!this.sharedData.UserData.identityInfo.email_verified) {
            this.bxUnverifiedAlertService.trigger(this.sharedData.UserData.user);
            return;
        }

        this.contactService.newReminder(contact);
    }

    // @ts-ignore TS7006
    deleteContact(contact) {
        if (contact.InternalUserId) return;

        this.contactService.deleteContact(contact);
    }

    showImportExcelModal() {
        this.contactService
            .importContacts()
            .then(() => {})
            .catch(() => {});
    }

    exportToExcel() {
        const options: ExportContactsOptions = {
            format: ExportFormat.XLSX,
            contactGroup: this.group?.split('|'),
            filter: this.search,
        };
        this.repoExportService.exportContacts(options);
    }

    // @ts-ignore TS7006
    resolveAction(action) {
        switch (action) {
            case 0: {
                this.showImportExcelModal();
                break;
            }
            case 1: {
                this.exportToExcel();
                break;
            }
        }
    }

    // Recycle Bin
    // @ts-ignore TS7006
    resolveSettingsAction(value) {
        switch (value) {
            case 0: {
                // active items
                this.setActiveView();
                break;
            }
            case 1: {
                // deleted items
                this.setDeletedView();
                break;
            }
        }
    }

    resetColumnWidths() {
        this.columnWidthsService.resetTableUsingTableName(Tables.Contacts);
    }

    setDeletedView() {
        this.title = `Deleted ${this.title}`;
        this.deletedView = true;
        this.storesService.updateSubpanelFilterStatus({ submenu: 'contacts', enabled: false });
        this.setDefaults();
        this.loadPage();
    }

    setActiveView() {
        this.deletedView = false;
        this.title = 'Contacts';
        this.storesService.updateSubpanelFilterStatus({ submenu: 'contacts', enabled: true });
        this.setDefaults();
        this.loadPage();
    }

    // ngx-datatable functions
    // @ts-ignore TS7006
    setPage(pageInfo) {
        this.page.pageNumber = pageInfo.offset;
        this.load();
    }

    // @ts-ignore TS7031
    onSelect({ selected }) {
        this.selected.splice(0, this.selected.length);
        // @ts-ignore TS2345
        this.selected.push(...selected);
    }

    // @ts-ignore TS7006
    getRowClass = (row) => {
        // @ts-ignore TS2339
        if (this.selected.length > 0 && this.selected.find((x) => x.ID_Contact === row.ID_Contact)) {
            return {
                'row-color': true,
            };
        } else {
            return {
                'row-color': false,
            };
        }
    };

    // @ts-ignore TS7006
    onSort(event) {
        // event was triggered, start sort sequence
        if (event.sorts[0].prop === this.table.sorts[0].prop && event.sorts[0].dir === 'desc') {
            this.defaultSort = {
                orderByDesc: event.sorts[0].prop,
            };
        } else {
            this.defaultSort = {
                orderBy: event.sorts[0].prop,
            };
        }
        this.loadPage();
    }

    // @ts-ignore TS7006
    recoveryContact(contact) {
        const params = {
            title: 'Recover Contact?',
            text: '',
        };

        this.dialogService.show(params).then(
            () => {
                this.contactApi.recoverContacts({ contactIds: [contact.ID_Contact] }).subscribe((x) => {
                    if (!(x.data?.recoverContacts.contact?.length ?? 0)) return;
                    this.logger.success('Contact has been recovered');
                    this.storesService.refreshContacts();
                    this.resetPageNumber();
                    this.loadPage();
                });
            },
            () => ({})
        );
    }

    // @ts-ignore TS7006
    resolveSimpleItemsSettingsAction(value) {
        switch (value) {
            case 0: {
                this.isLoading = true;
                this.contactsWidths = {} as ColumnWidths;
                this.resetColumnWidths();
                const tempRows = [...this.rows];
                this.rows.length = 0;
                setTimeout(() => {
                    this.rows = [...tempRows];
                    this.isLoading = false;
                }, 100);
                break;
            }
        }
    }

    // @ts-ignore TS7006
    onResize(event) {
        const contactsColWidths = {};
        // @ts-ignore TS7006
        this.table._internalColumns.forEach((row) => {
            const colKey = row.prop;
            const colWidth = row.width;
            // @ts-ignore TS7053
            contactsColWidths[colKey] = colWidth;
        });
        this.localStorageService.saveObject('contacts-table', contactsColWidths);
    }

    private resetPageNumber() {
        if (this.tablesHelperService.isResetPageNumber(this.page.totalElements - 1, this.page.size, this.page.pageNumber))
            this.page.pageNumber--;
    }
}
